import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import './WhatsAppSettings.css';

/** The box is one number per line; the document stores an array. Blank lines
 *  and stray spaces are the operator's, not data. */
function splitPhones(text: string): string[] {
  return text.split(/[\n,]/).map(p => p.trim()).filter(Boolean);
}

/**
 * WhatsApp Settings — admin-editable non-secret WhatsApp config.
 *
 * Lives in the `whatsapp-settings` document in the settings container.
 * Secrets (access token, app secret, verify token) remain in Azure Function
 * env vars and are not editable here.
 */
export function WhatsAppSettings() {
  const queryClient = useQueryClient();

  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [progressReportTemplate, setProgressReportTemplate] = useState('');
  const [otpTemplate, setOtpTemplate] = useState('');
  const [otpTemplateLanguage, setOtpTemplateLanguage] = useState('');
  // One per line in the box; stored as an array. See WhatsAppSettingsRepository.
  const [alertPhones, setAlertPhones] = useState('');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['whatsappSettings'],
    queryFn: adminApi.getWhatsAppSettings,
  });

  const templates = data?.supportedProgressReportTemplates ?? [];

  useEffect(() => {
    if (data) {
      setPhoneNumberId(data.settings?.phoneNumberId ?? '');
      // Default to the first supported template when none is stored yet.
      setProgressReportTemplate(data.settings?.progressReportTemplate ?? templates[0]?.name ?? '');
      setOtpTemplate(data.settings?.otpTemplate ?? '');
      setOtpTemplateLanguage(data.settings?.otpTemplateLanguage ?? '');
      setAlertPhones((data.settings?.alertPhones ?? []).join('\n'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      adminApi.updateWhatsAppSettings({
        phoneNumberId: phoneNumberId.trim(),
        progressReportTemplate: progressReportTemplate.trim(),
        otpTemplate: otpTemplate.trim(),
        otpTemplateLanguage: otpTemplateLanguage.trim(),
        alertPhones: splitPhones(alertPhones),
      }),
    onMutate: () => {
      setSaveStatus('saving');
      setSaveError('');
    },
    onSuccess: () => {
      setSaveStatus('saved');
      queryClient.invalidateQueries({ queryKey: ['whatsappSettings'] });
      setTimeout(() => setSaveStatus('idle'), 2000);
    },
    onError: (err) => {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    },
  });

  const dirty =
    phoneNumberId !== (data?.settings?.phoneNumberId ?? '') ||
    progressReportTemplate !== (data?.settings?.progressReportTemplate ?? templates[0]?.name ?? '') ||
    otpTemplate !== (data?.settings?.otpTemplate ?? '') ||
    otpTemplateLanguage !== (data?.settings?.otpTemplateLanguage ?? '') ||
    splitPhones(alertPhones).join('\n') !== (data?.settings?.alertPhones ?? []).join('\n');

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2>WhatsApp</h2>
      </div>

      {isLoading ? (
        <div className="loading">Loading WhatsApp settings…</div>
      ) : (
        <div className="wa-form">
          <div className="wa-field">
            <label className="feature-field-label" htmlFor="wa-phoneNumberId">Phone Number ID</label>
            <input
              id="wa-phoneNumberId"
              type="text"
              className="feature-field-input wa-input-mono"
              value={phoneNumberId}
              onChange={(e) => setPhoneNumberId(e.target.value)}
              placeholder="123456789012345"
            />
            <p className="wa-field-help">
              Meta-assigned Phone Number ID (from Meta Business Manager → WhatsApp → API Setup).
            </p>
          </div>

          <div className="wa-field">
            <label className="feature-field-label" htmlFor="wa-progressReportTemplate">Progress-report template</label>
            <select
              id="wa-progressReportTemplate"
              className="feature-field-input"
              value={progressReportTemplate}
              onChange={(e) => setProgressReportTemplate(e.target.value)}
            >
              {templates.map((t) => (
                <option key={t.name} value={t.name}>{t.name} — {t.label}</option>
              ))}
            </select>
            <p className="wa-field-help">
              Template used for progress reports (per-environment).
            </p>
          </div>

          <div className="wa-field">
            <label className="feature-field-label" htmlFor="wa-otpTemplate">Login OTP template</label>
            <input
              id="wa-otpTemplate"
              type="text"
              className="feature-field-input wa-input-mono"
              value={otpTemplate}
              onChange={(e) => setOtpTemplate(e.target.value)}
              placeholder="login_otp"
            />
            <p className="wa-field-help">
              Meta template that carries the login code. It must be approved under the
              <strong> Authentication</strong> category — a code sent on a Utility or Marketing
              template is rejected. Leave blank to use the built-in default (<code>login_otp</code>).
              Only used when App Settings → phone OTP transport is set to WhatsApp; otherwise codes go by SMS.
            </p>
          </div>

          <div className="wa-field">
            <label className="feature-field-label" htmlFor="wa-otpTemplateLanguage">Login OTP template language</label>
            <input
              id="wa-otpTemplateLanguage"
              type="text"
              className="feature-field-input wa-input-mono"
              value={otpTemplateLanguage}
              onChange={(e) => setOtpTemplateLanguage(e.target.value)}
              placeholder="en"
            />
            <p className="wa-field-help">
              The language code Meta registered the template under. Blank means <code>en</code>.
              Meta treats <code>en</code> (&ldquo;English&rdquo;) and <code>en_US</code>
              (&ldquo;English (US)&rdquo;) as different templates, and sending against the wrong
              one fails as though the template did not exist &mdash; so if approval comes back
              under <code>en_US</code>, put that here.
            </p>
          </div>

          <div className="wa-field">
            <label className="feature-field-label" htmlFor="wa-alertPhones">On-call WhatsApp numbers</label>
            <textarea
              id="wa-alertPhones"
              className="feature-field-input wa-input-mono"
              rows={3}
              value={alertPhones}
              onChange={(e) => setAlertPhones(e.target.value)}
              placeholder="+919876543210"
            />
            <p className="wa-field-help">
              Who gets paged on WhatsApp by an operational alert &mdash; today, a thumbs-down on
              an analysis. One E.164 number per line, <code>+</code> and country code included.
              This is deliberately a NUMBER and not &ldquo;every admin&rdquo;: an alert has a
              rota, and resolving it from admin accounts means a colleague who once saved a
              phone number is paged too, and a new admin silently joins. Leave it blank and
              nobody is paged &mdash; email and in-app are unaffected either way.
            </p>
          </div>

          <div className="feature-actions">
            <button
              type="button"
              className="feature-save-btn"
              onClick={() => saveMutation.mutate()}
              disabled={!dirty || saveStatus === 'saving'}
            >
              {saveStatus === 'saving' ? 'Saving…' : 'Save'}
            </button>
            {saveStatus === 'saved' && <span className="status-saved">Saved.</span>}
            {saveStatus === 'error' && <span className="status-error">Error: {saveError}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
